<template>
  <BaseTinderOverlay
    :is-open="$stores.userAlert.showOverlay"
    content-class="lg:max-w-[588px] mx-auto"
    @close="onClose"
  >
    <template #header>
      <div class="lg:max-w-[788px] mx-auto">
        <div
          class="mb-6 flex items-center justify-center gap-2 text-[22px] lg:text-[32px] leading-10 font-bold"
        >
          <div
            :class="[
              $stores.userAlert.unreadMissionsCount > 0
                ? 'bg-[#F93F42] text-white'
                : 'bg-[#4B9F6C] text-white',
              'flex-none font-bold h-8 w-8 text-base lg:h-10 lg:w-10 lg:text-2xl rounded-full flex items-center justify-center ',
            ]"
          >
            {{ $stores.userAlert.unreadMissionsCount }}
          </div>
          <div class="text-white font-bold">
            {{
              $filters.pluralize(
                $stores.userAlert.unreadMissionsCount,
                'nouvelle mission',
                'nouvelles missions',
                false
              )
            }}
          </div>
        </div>
        <div class="hidden lg:flex lg:mt-4 lg:flex-wrap lg:justify-center lg:gap-2">
          <DsfrTag v-if="!isDistance" size="md"
            >{{ alert.conditions.city }} ({{ alert.conditions.zip }})</DsfrTag
          >
          <DsfrTag v-if="!isDistance" size="md"
            >À moins de {{ alert.conditions.radius / 1000 }} km</DsfrTag
          >
          <DsfrTag size="md">{{
            $filters.label(alert.conditions.commitment, 'commitment')
          }}</DsfrTag>
          <DsfrTag v-for="activityId in activities" :key="activityId" size="md">
            {{ $filters.activities(activityId) }}
          </DsfrTag>
          <DsfrTag v-if="moreActivities.length > 0" size="md"
            >+{{ moreActivities.length }}
            {{ moreActivities.length > 1 ? 'autres activités' : 'autre activité' }}</DsfrTag
          >
        </div>
      </div>
    </template>

    <template v-if="missions.length > 0" #footer>
      <button
        class="fixed z-[110] bottom-6 left-1/2 -translate-x-[calc(50%+51px)] shadow-overlap group flex justify-center items-center h-[80px] w-[80px] rounded-full bg-gradient-to-b from-white to-[#EFEFEF] border border-[#DDDDDD] hover:scale-105 active:scale-95 transition"
        @click="passMission"
        :disabled="buttonsIsDisabled"
      >
        <RiCloseFill class="text-[#3A3A3A] stroke-[#161616] h-8 stroke-2 transition" />
      </button>
      <button
        class="fixed z-[110] bottom-6 right-1/2 translate-x-[calc(50%+51px)] shadow-overlap group flex justify-center items-center h-[80px] w-[80px] rounded-full bg-gradient-to-b from-white to-[#EFEFEF] border border-[#DDDDDD] hover:scale-105 active:scale-95 transition"
        @click="addMissionToFavorite"
        :disabled="buttonsIsDisabled"
      >
        <RiHeartFill class="text-[#F93F42] h-8 stroke-[#C9191E] transition" />
      </button>
    </template>

    <template v-if="!$stores.userAlert.isFetching">
      <template v-if="missions.length > 0">
        <div class="relative h-[950px] lg:h-[750px] mt-[50px] lg:mt-[75px] mb-[150px]">
          <div
            v-for="(mission, i) in missions"
            :key="mission.pivot.id"
            :ref="`card-alert-${mission.pivot.id}`"
            :class="[
              'absolute overflow-hidden lg:min-h-[867px]',
              { 'bg-white w-[100%] top-0 z-[100] ': i === 0 },
              {
                'bg-[#E4E4EF] left-[5%] w-[90%] top-[-16px] z-[90] max-h-[800px] lg:max-h-[800px]':
                  i === 1,
              },
              {
                'bg-[#9999D3] left-[10%] w-[80%] top-[-32px] z-[80] max-h-[800px] lg:max-h-[800px]':
                  i === 2,
              },
              {
                'bg-[#64649f] left-[15%] w-[70%] top-[-48px] z-[70] opacity-0 max-h-[800px] lg:max-h-[800px]':
                  i === 3,
              },
            ]"
          >
            <CardFicheMission :mission="mission" />
          </div>
        </div>
      </template>
      <template v-else>
        <div
          class="mt-10 w-full max-w-[588px] bg-white flex flex-col justify-center items-center text-center p-10"
        >
          <img src="/images/icons/dsfr/success.svg" alt="" class="flex-none w-[80px]" />
          <div class="mt-4 mb-4 text-[#161616] text-[24px] lg:text-[28px] font-bold leading-9">
            Vous avez vu toutes<br class="hidden lg:block" />
            les nouvelles missions
          </div>
          <div class="text-[#666666] text-base lg:text-lg leading-7">
            Consultez vos favoris ou effectuez une nouvelle recherche et proposez votre aide.
          </div>
          <div class="mt-10">
            <DsfrButton @click="goToFavoris" size="lg">Voir mes favoris</DsfrButton>
          </div>
          <div class="mt-4">
            <DsfrLink @click="goToSearch" class="text-jva-blue-500 text-base lg:text-lg"
              >Chercher d’autres missions</DsfrLink
            >
          </div>
        </div>
      </template>
    </template>
    <template v-else>
      <div class="pt-24">
        <CustomLoadingIndicator icon-class="text-white h-12 w-12" />
      </div>
    </template>
  </BaseTinderOverlay>
</template>

<script>
import anime from 'animejs'
import CardFicheMission from '@/components/card/CardFicheMission'

export default defineNuxtComponent({
  components: {
    CardFicheMission,
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      buttonsIsDisabled: false,
    }
  },
  computed: {
    alert() {
      return this.$stores.userAlert.selectedAlert
    },
    isDistance() {
      return this.alert.conditions.type_missions === 'distance'
    },
    activities() {
      return this.alert.conditions.activities.slice(0, 3)
    },
    moreActivities() {
      return this.alert.conditions.activities.slice(3)
    },
    missions() {
      return this.$stores.userAlert.unreadMissions.slice(0, 4)
    },
    mission() {
      return this.missions[0]
    },
  },
  methods: {
    goToFavoris() {
      this.onClose()
      this.$router.push('/profile/favoris')
    },
    goToSearch() {
      this.onClose()
      this.$router.push('/missions-benevolat')
    },
    animate(translateXValue, rotateValue) {
      const cards = this.missions.map(
        (mission, i) => this.$refs[`card-alert-${mission.pivot.id}`][0]
      )

      anime
        .timeline({
          easing: 'easeInOutQuad',
          duration: 750,
          complete: () => {
            this.$stores.userAlert.nextMission()
            this.buttonsIsDisabled = false
          },
        })
        .add(
          {
            targets: cards[0],
            translateX: translateXValue,
            rotate: rotateValue,
            opacity: 0,
            duration: 750,
            easing: 'easeInOutQuad',
          },
          0
        )
        .add(
          {
            targets: cards[1],
            width: ['90%', '100%'],
            left: ['5%', '0%'],
            top: ['-16px', '0px'],
            duration: 250,
            backgroundColor: ['#E4E4EF', '#FFFFFF'],
            easing: 'easeInOutQuad',
          },
          0
        )
        .add(
          {
            targets: cards[2],
            width: ['80%', '90%'],
            left: ['10%', '5%'],
            top: ['-32px', '-16px'],
            backgroundColor: ['#9999D3', '#E4E4EF'],
            duration: 250,
            easing: 'easeInOutQuad',
          },
          0
        )
        .add(
          {
            targets: cards[3],
            width: ['70%', '80%'],
            left: ['15%', '10%'],
            top: ['-48px', '-32px'],
            backgroundColor: ['#64649f', '#9999D3'],
            opacity: [0, 1],
            duration: 250,
            easing: 'easeInOutQuad',
          },
          0
        )
    },
    passMission() {
      this.buttonsIsDisabled = true
      this.$stores.userAlert.deleteUserAlertMission(this.alert.id, this.mission.pivot.id)
      this.animate(-750, -30)
    },
    addMissionToFavorite() {
      this.buttonsIsDisabled = true
      this.$stores.userAlert.addMissionToFavorite(this.alert.id, this.mission.pivot.id)
      this.animate(750, 30)
      this.$plausible.trackEvent('Alerte - Mission ajoutée aux favoris')
    },
    onClose() {
      this.$stores.auth.fetchUser()
      this.$stores.userAlert.fetch()
      this.$router.push('/profile/alertes')
      this.$stores.userAlert.closeOverlay()
    },
  },
})
</script>
